import { useState, useEffect } from 'react';
import { PageHeader, Badge, type ToneType } from './shared';
import { Icon } from './ui/icons';

interface RiskAcceptance {
  id: string;
  title: string;
  description: string;
  risk_level: 'critical' | 'high' | 'medium' | 'low';
  status: 'pending' | 'approved' | 'rejected' | 'expired' | 'under_review';
  submitted_by: string;
  submitted_at: string;
  reviewed_by?: string;
  reviewed_at?: string;
  expires_at?: string;
  affected_controls: string[];
  business_justification: string;
  mitigation_measures: string[];
  approval_conditions?: string[];
}

const mockAcceptances: RiskAcceptance[] = [
  {
    id: 'RA-2026-021',
    title: 'TensorFlow Library Patch Delay',
    description: 'Temporary acceptance of known TensorFlow vulnerability CVE-2024-12345 while waiting for compatible patch',
    risk_level: 'medium',
    status: 'approved',
    submitted_by: 'engineering-team',
    submitted_at: '2026-05-18T14:00:00Z',
    reviewed_by: 'Tom Holland',
    reviewed_at: '2026-05-18T22:00:00Z',
    expires_at: '2026-06-15T23:59:59Z',
    affected_controls: ['DEP01'],
    business_justification: 'Critical ML model deployment scheduled for next week. Patched version introduces breaking changes that would delay release by 6 weeks.',
    mitigation_measures: ['Network segmentation', 'Enhanced monitoring', 'Restricted model access'],
    approval_conditions: ['Monthly security review', 'Patch within 30 days']
  },
  {
    id: 'RA-2026-020',
    title: 'Legacy Database Access',
    description: 'Continued use of legacy authentication system for customer-facing database',
    risk_level: 'high',
    status: 'under_review',
    submitted_by: 'product-team',
    submitted_at: '2026-05-20T09:30:00Z',
    affected_controls: ['AUTH01', 'RBAC01'],
    business_justification: 'Customer contract requires specific authentication method. Migration to modern auth would break SLA commitments.',
    mitigation_measures: ['Additional audit logging', 'VPN-only access', 'Quarterly penetration testing']
  },
  {
    id: 'RA-2026-019',
    title: 'Development Environment Anonymization',
    description: 'Delayed implementation of data anonymization in development environments',
    risk_level: 'medium',
    status: 'pending',
    submitted_by: 'data-team',
    submitted_at: '2026-05-19T11:15:00Z',
    affected_controls: ['DAZ01'],
    business_justification: 'Anonymization SDK testing reveals data quality issues. Need additional 4 weeks for proper validation.',
    mitigation_measures: ['Access restrictions', 'Data encryption', 'Regular access audits']
  },
  {
    id: 'RA-2026-018',
    title: 'S3 Bucket Public Read Access',
    description: 'Temporary public read access for marketing asset distribution',
    risk_level: 'low',
    status: 'expired',
    submitted_by: 'marketing-team',
    submitted_at: '2026-04-01T10:00:00Z',
    reviewed_by: 'Security Team',
    reviewed_at: '2026-04-01T16:00:00Z',
    expires_at: '2026-05-01T23:59:59Z',
    affected_controls: ['SEC01'],
    business_justification: 'Campaign assets need to be accessible for external partners and CDN integration.',
    mitigation_measures: ['Asset review process', 'Temporary access only', 'No sensitive data'],
    approval_conditions: ['Weekly asset audit', '30-day expiration']
  },
  {
    id: 'RA-2026-017',
    title: 'Contractor VPN Access',
    description: 'Extended VPN access for external security consultants',
    risk_level: 'medium',
    status: 'rejected',
    submitted_by: 'security-ops',
    submitted_at: '2026-05-10T14:45:00Z',
    reviewed_by: 'Tom Holland',
    reviewed_at: '2026-05-12T09:00:00Z',
    affected_controls: ['AUTH01', 'RBAC01'],
    business_justification: 'Security audit requires extended access to production monitoring systems.',
    mitigation_measures: ['Supervised access only', 'Detailed logging', 'Restricted scope']
  }
];

const riskLevelTone: Record<string, ToneType> = {
  critical: 'crit',
  high: 'warn',
  medium: 'info',
  low: 'muted'
};

const statusTone: Record<string, ToneType> = {
  pending: 'warn',
  approved: 'ok',
  rejected: 'crit',
  expired: 'muted',
  under_review: 'info'
};

function formatTimeAgo(timestamp: string): string {
  const now = new Date();
  const eventTime = new Date(timestamp);
  const diffMs = now.getTime() - eventTime.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays} days ago`;
}

function formatExpiryStatus(expiresAt: string): { text: string; tone: ToneType } {
  const now = new Date();
  const expiryDate = new Date(expiresAt);
  const diffMs = expiryDate.getTime() - now.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMs < 0) return { text: 'Expired', tone: 'crit' };
  if (diffDays === 0) return { text: 'Expires today', tone: 'warn' };
  if (diffDays <= 7) return { text: `${diffDays} days left`, tone: 'warn' };
  return { text: `${diffDays} days left`, tone: 'ok' };
}

export default function RiskAcceptances() {
  const [acceptances, setAcceptances] = useState<RiskAcceptance[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('active');
  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    const loadAcceptances = async () => {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 500));
      setAcceptances(mockAcceptances);
      setLoading(false);
    };
    
    loadAcceptances();
  }, []);

  const filteredAcceptances = acceptances.filter(acceptance => {
    const isActive = ['pending', 'approved', 'under_review'].includes(acceptance.status);
    const matchesFilter = filter === 'all' || 
      (filter === 'active' && isActive) ||
      acceptance.status === filter ||
      acceptance.risk_level === filter;
    
    const matchesSearch = search === '' || 
      acceptance.title.toLowerCase().includes(search.toLowerCase()) ||
      acceptance.description.toLowerCase().includes(search.toLowerCase()) ||
      acceptance.submitted_by.toLowerCase().includes(search.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  const acceptanceStats = {
    total: acceptances.length,
    pending: acceptances.filter(a => a.status === 'pending').length,
    approved: acceptances.filter(a => a.status === 'approved').length,
    expiring_soon: acceptances.filter(a => {
      if (!a.expires_at) return false;
      const diffMs = new Date(a.expires_at).getTime() - new Date().getTime();
      return diffMs > 0 && diffMs <= 7 * 24 * 60 * 60 * 1000; // 7 days
    }).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center gap-2 text-ink/60">
          <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
          <span className="font-display text-[16px]">Loading risk acceptances...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Risk Acceptances"
        subtitle="Document and approve risk acceptance decisions for compliance and security controls."
        right={
          <div className="flex items-center gap-3">
            <button className="h-10 px-4 rounded-[10px] border border-[#F3E4C8] bg-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:border-ink/40">
              <Icon name="download" size={14} stroke="#121111" /> Export
            </button>
            <button className="h-10 px-4 rounded-[10px] bg-[#FA500E] text-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:opacity-90">
              <Icon name="plus" size={14} stroke="white" /> New Risk Acceptance
            </button>
          </div>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Total</div>
          <div className="font-display font-bold text-ink mt-0.5 text-[24px]">{acceptanceStats.total}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Pending</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#CA9401' }}>{acceptanceStats.pending}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Approved</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#159D4C' }}>{acceptanceStats.approved}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Expiring Soon</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#F40B0B' }}>{acceptanceStats.expiring_soon}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Icon name="search" size={16} className="text-ink/40" />
          <input
            type="text"
            placeholder="Search risk acceptances..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="font-display text-[14px] text-ink placeholder-ink/40 border-none outline-none bg-transparent"
          />
        </div>
        
        <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="font-display text-[14px] text-ink border border-[#F3E4C8] rounded-[6px] px-3 py-2 bg-white"
          >
            <option value="active">Active</option>
            <option value="all">All Acceptances</option>
            <option value="pending">Pending Review</option>
            <option value="approved">Approved</option>
            <option value="under_review">Under Review</option>
            <option value="expired">Expired</option>
            <option value="critical">Critical Risk</option>
            <option value="high">High Risk</option>
          </select>
        </div>
      </div>

      {/* Risk Acceptances List */}
      <div className="rounded-[10px] border border-[#F3E4C8] bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-[#F7EDDB] bg-[#FCF8F1] font-display font-bold text-[11px] uppercase tracking-[0.1em] text-ink/60">
          Risk Acceptances ({filteredAcceptances.length})
        </div>
        
        <ul>
          {filteredAcceptances.map((acceptance) => (
            <li 
              key={acceptance.id} 
              className="px-5 py-4 hover:bg-[#FCF8F1] border-b border-[#F7EDDB] last:border-0"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-display font-bold text-ink text-[16px]">{acceptance.title}</h3>
                    <Badge tone={riskLevelTone[acceptance.risk_level]} dot className="capitalize">
                      {acceptance.risk_level} Risk
                    </Badge>
                    <Badge tone={statusTone[acceptance.status]} className="capitalize">
                      {acceptance.status.replace('_', ' ')}
                    </Badge>
                    {acceptance.expires_at && acceptance.status === 'approved' && (
                      (() => {
                        const expiry = formatExpiryStatus(acceptance.expires_at);
                        return <Badge tone={expiry.tone}>{expiry.text}</Badge>;
                      })()
                    )}
                  </div>
                  
                  <p className="font-display text-[14px] text-ink/65 mb-3 line-clamp-2">
                    {acceptance.description}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 text-[12px] text-ink/50 mb-3">
                    <div>
                      <strong>Submitted by:</strong> @{acceptance.submitted_by}
                    </div>
                    <div>
                      <strong>Submitted:</strong> {formatTimeAgo(acceptance.submitted_at)}
                    </div>
                    {acceptance.reviewed_by && (
                      <div>
                        <strong>Reviewed by:</strong> {acceptance.reviewed_by}
                      </div>
                    )}
                    {acceptance.reviewed_at && (
                      <div>
                        <strong>Reviewed:</strong> {formatTimeAgo(acceptance.reviewed_at)}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div>
                      <span className="font-display font-semibold text-[12px] text-ink/70">Affected Controls:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {acceptance.affected_controls.map(control => (
                          <span key={control} className="inline-flex items-center px-2 py-1 rounded-[4px] bg-[#F7EDDB] text-[#CA9401] font-data text-[10px] font-semibold">
                            {control}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <span className="font-display font-semibold text-[12px] text-ink/70">Business Justification:</span>
                      <p className="font-display text-[12px] text-ink/65 mt-1 line-clamp-2">
                        {acceptance.business_justification}
                      </p>
                    </div>
                    
                    <div>
                      <span className="font-display font-semibold text-[12px] text-ink/70">Mitigation Measures:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {acceptance.mitigation_measures.map((measure, i) => (
                          <span key={i} className="inline-flex items-center px-2 py-1 rounded-[4px] bg-[#C6F3D8] text-[#159D4C] font-display text-[10px] font-semibold">
                            {measure}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  {acceptance.status === 'pending' && (
                    <>
                      <button className="h-8 px-3 rounded-[6px] bg-[#159D4C] text-white font-display font-bold text-[12px] hover:opacity-90">
                        Approve
                      </button>
                      <button className="h-8 px-3 rounded-[6px] bg-[#F40B0B] text-white font-display font-bold text-[12px] hover:opacity-90">
                        Reject
                      </button>
                    </>
                  )}
                  <button className="h-8 px-3 rounded-[6px] border border-[#F3E4C8] bg-white font-display font-bold text-[12px] text-ink hover:border-ink/40">
                    View Details
                  </button>
                  <button className="h-8 w-8 rounded-[6px] border border-[#F3E4C8] bg-white grid place-items-center hover:border-ink/40">
                    <Icon name="more-horizontal" size={14} stroke="#121111" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}